/**
 * ADP SM Cookie Refresh — keeps the SMSESSION cookie alive for multi-app SSO.
 *
 * Per ADP docs the SM Cookie Refresh API must be called every 60 seconds while
 * the user is active. A non-success response means the ADP session expired
 * (e.g. user was idle for 20 min or logged out of another ADP app), and our
 * app must log the user out.
 *
 * Behaviour:
 * - Polls every 60 s while the tab is visible (skips hidden tabs).
 * - On tab focus: fires an immediate refresh to validate the session.
 * - On tab blur: pauses the interval to avoid unnecessary requests.
 * - Any failure (network, non-200, Status !== 'Success') → logout.
 *
 * Uses native fetch (not axios) because the request goes cross-origin to ADP's
 * domain and must include the SMSESSION cookie via credentials: 'include'.
 */
import { AIM_SM_COOKIE_REFRESH_URL } from '@/types';
import { clearTokens, isOidcSession } from '@/utils/helpers';

const SM_COOKIE_REFRESH_INTERVAL_MS = 60_000;

let intervalId: ReturnType<typeof setInterval> | null = null;
let listening = false;

const clearSmRefreshInterval = () => {
  if (intervalId !== null) {
    clearInterval(intervalId);
    intervalId = null;
  }
};

const startInterval = () => {
  if (intervalId !== null) return;
  intervalId = setInterval(refreshSmCookie, SM_COOKIE_REFRESH_INTERVAL_MS);
};

const handleRefreshFailure = () => {
  stopSmCookieRefresh();
  clearTokens();
  window.location.href = '/v2/welcome-back/';
};

const refreshSmCookie = async () => {
  if (!isOidcSession()) {
    stopSmCookieRefresh();
    return;
  }

  try {
    const response = await fetch(AIM_SM_COOKIE_REFRESH_URL, {
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) {
      handleRefreshFailure();
      return;
    }

    const data = await response.json();

    if (data.Status !== 'Success') {
      handleRefreshFailure();
    }
  } catch {
    handleRefreshFailure();
  }
};

// On tab focus: immediately validate the ADP session and resume polling.
// On tab blur: pause polling to avoid wasted requests.
const onVisibilityChange = () => {
  if (document.visibilityState === 'visible') {
    refreshSmCookie();
    startInterval();
  } else {
    clearSmRefreshInterval();
  }
};

export const startSmCookieRefresh = (): void => {
  if (!isOidcSession() || !AIM_SM_COOKIE_REFRESH_URL) return;

  if (document.visibilityState === 'visible') {
    refreshSmCookie();
    startInterval();
  }

  if (!listening) {
    document.addEventListener('visibilitychange', onVisibilityChange);
    listening = true;
  }
};

export const stopSmCookieRefresh = (): void => {
  clearSmRefreshInterval();

  if (listening) {
    document.removeEventListener('visibilitychange', onVisibilityChange);
    listening = false;
  }
};
